import { HorizontalOriginOption, InlineAlertWidget, VerticalOriginOption } from '@tableau/widgets';
import { InlineAlertType } from '@tableau/widgets/lib/src/widgets/InlineAlert/InlineAlertBase';
import { InfoIcon } from '@tableau/widgets/lib/src/widgets/Tooltip/Tooltip';
import * as React from 'react';
import { Component } from 'react';
import * as _ from 'underscore';

import { Messages } from '~src/codegen/Localize';
import { ButtonWithUberDialog } from '~src/common/react/components/ButtonWithUberDialog';
import { interpolateString } from '~src/common/utilities/StringInterpolation';
import { openReactActionDialog, ReactActionDialogBodyProps } from '~src/commonInfra/dialogs/ReactActionDialog';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { ResultHelper } from '~src/httpClient/utils/ResultHelper';
import WebHttpClient from '~src/httpClient/WebHttpClient';
import { cssClass } from '~src/style';
import { ToasterService, ToastType } from '~src/ts/services/ToasterService';

import { IResourceGovernanceServerSettings, IResourceGovernanceServerSettingsKey, IResourceGovernanceSettingsKey, UpdateResourceGovernanceServerSettingsRequest } from '../types/ResourceGovernance';
import { ConcurrentJobLimitRowProps, ResourceGovernanceLimitsRow, rowShouldAlert } from './ResourceGovernanceLimitsRow';

export const cssClassPrefix = 'resource-governance-dialog-edit-default';
const leftPadClass = cssClass(cssClassPrefix, 'left-pad', {
  paddingLeft: 20,
  verticalAlign: 'middle',
});
export const topLeftPadClass = cssClass(cssClassPrefix, 'left-pad', {
  paddingLeft: 20,
  paddingTop: 10,
  verticalAlign: 'middle',
});
const flexApart = cssClass(cssClassPrefix, 'flex-apart', {
  paddingLeft: 20,
  verticalAlign: 'middle',
  display: 'flex',
  justifyContent: 'space-between',
});
const tooltipStyle = cssClass(cssClassPrefix, 'tooltip', {
  width: 300,
  display: 'inline-block',
  whiteSpace: 'pre-line',
});
const rowKeys: IResourceGovernanceSettingsKey[] = ['extractsConcurrencyLimit', 'subscriptionsConcurrencyLimit', 'flowsConcurrencyLimit', 'runtimeLimit'];
export type ResourceGovernanceEditDefaultLimitsDialogProps = {
  title: string;
} & IResourceGovernanceServerSettings;

function createResetTimeRows() {
  const description = interpolateString(Messages.resourceGovernanceResetTimeHeader(), {
    LIMIT_TYPE: () => <b key={`bold-text-${Messages.resourceGovernanceResetTime()}`}>{Messages.resourceGovernanceResetTime()}</b>
  });
  return <tr key={`row-${Messages.resourceGovernanceResetTime()}`}>
    <td className={topLeftPadClass}>{description}</td>
    <td></td>
    <td className={topLeftPadClass}>{Messages.resourceGovernanceMidnightUTC()}</td>
  </tr>;
}

export type ReactActionResourceGovernanceEditDefaultLimitsDialogProps = ReactActionDialogBodyProps<ResourceGovernanceEditDefaultLimitsDialogProps, IResourceGovernanceServerSettings>;
export class ResourceGovernanceDefaultLimitsDialog extends Component<ReactActionResourceGovernanceEditDefaultLimitsDialogProps> {

  constructor(props: ReactActionResourceGovernanceEditDefaultLimitsDialogProps) {
    super(props);
    this.updateDialog = this.updateDialog.bind(this);
  }

  updateDialog = (key: IResourceGovernanceSettingsKey) => (value: string) => {
    const isValidNum = /^\d+\.?\d*$/.test(value);
    value === undefined
      ? this.props.updateDialogState({ [key]: value })
      : this.props.updateDialogState({ [key]: isValidNum ? Number(value) : NaN });
  }

  public render() {
    const headerRow = <tr key='header'>
      <td><b>{Messages.resourceGovernanceConcurrentJobsLimitsHeader()}</b></td>
      <td className={leftPadClass}><b>{Messages.resourceGovernanceNoLimitValue()}</b></td>
      <td className={flexApart}>
        <b>{Messages.resourceGovernanceConcurrencyDeaultLimitsHeader()}</b>
        {<ButtonWithUberDialog
          renderButton={showDialog => <InfoIcon onClick={showDialog} />}
          uberDialogContent={<span className={tooltipStyle}>
            {Messages.resourceGovernanceConcurrencyLimitsTooltip()}
            <a href='http://example.com'>{Messages.learnMore()}</a>
          </span>}
          anchorOrigin={{ horizontal: HorizontalOriginOption.right, vertical: VerticalOriginOption.top }}
          stackedOrigin={{ horizontal: HorizontalOriginOption.left, vertical: VerticalOriginOption.top }}
        />}
      </td>
    </tr>;
    const rowsInfo: ConcurrentJobLimitRowProps[] = [{
      limitType: Messages.resourceGovernanceConcurrencyExtractRefreshes(),
      update: this.updateDialog('extractsConcurrencyLimit'),
      value: this.props.dialogState.extractsConcurrencyLimit,
      originalValue: this.props.extractsConcurrencyLimit,
      description: Messages.resourceGovernanceConcurrencyDescription(),
      testId: 'extractsConcurrencyLimit',
    },
    {
      limitType: Messages.resourceGovernanceConcurrencySubscriptions(),
      update: this.updateDialog('subscriptionsConcurrencyLimit'),
      value: this.props.dialogState.subscriptionsConcurrencyLimit,
      originalValue: this.props.subscriptionsConcurrencyLimit,
      description: Messages.resourceGovernanceConcurrencyDescription(),
      testId: 'subscriptionsConcurrencyLimit',
    },
    {
      limitType: Messages.resourceGovernanceConcurrencyFlows(),
      update: this.updateDialog('flowsConcurrencyLimit'),
      value: this.props.dialogState.flowsConcurrencyLimit,
      originalValue: this.props.flowsConcurrencyLimit,
      description: Messages.resourceGovernanceConcurrencyDescription(),
      testId: 'flowsConcurrencyLimit',
    }];
    const concurrentLimitRows = rowsInfo.map(row => <ResourceGovernanceLimitsRow key={row.limitType} {...row} />);
    const anyRowAlerting = _.any(rowsInfo, row => rowShouldAlert(row.value, row.originalValue));
    const alertExplanation = <InlineAlertWidget
      testId='decreased-limit-alert'
      type={InlineAlertType.Warning}
    >
      {Messages.resourceGovernanceConcurrencyDecreasedLimitAlert()}
    </InlineAlertWidget>;
    const dailyLimitHeader = <tr><td style={{ paddingTop: 12 }}><b>{Messages.resourceGovernanceDailyLimitsHeader()}</b></td></tr>;
    const runTimeLimitRowProps: ConcurrentJobLimitRowProps = {
      limitType: Messages.resourceGovernanceBackgrounderHours(),
      update: this.updateDialog('runtimeLimit'),
      value: this.props.dialogState.runtimeLimit,
      originalValue: this.props.runtimeLimit,
      description: Messages.resourceGovernanceBackgrounderDescription(),
      testId: 'runtimeLimit'
    };
    const resetTime = createResetTimeRows();

    return <>
      <table><tbody>
        {headerRow}
        {concurrentLimitRows}
        {dailyLimitHeader}
        <ResourceGovernanceLimitsRow {...runTimeLimitRowProps} />
        {resetTime}
      </tbody></table>
      {anyRowAlerting && alertExplanation}
    </>;
  }
}

export async function saveDefaultLimitsSettings(props: ResourceGovernanceEditDefaultLimitsDialogProps, state: IResourceGovernanceServerSettings): Promise<any> {
  const updatedSettings = Object.keys(state).map((key: IResourceGovernanceServerSettingsKey) => {
    const value = state[key] === undefined ? null : state[key];
    return { [key]: value };
  });
  const flattenedSettings: IResourceGovernanceServerSettings = _.assign({}, ...updatedSettings) as { [K in IResourceGovernanceServerSettingsKey]: number | null };

  WebHttpClient.sendRequest(new UpdateResourceGovernanceServerSettingsRequest({ resourceGovernanceServerSettings: flattenedSettings }))
    .then((result: IResult) => {
      if (ResultHelper.hasErrors(result)) {
        ToasterService.instance.pop({
          type: ToastType.Error,
          text: Messages.resourceGovernanceServerSettingsSaveFailed(),
          actionMessage: Messages.resourceGovernanceLearnMoreMessage(),
          actionHref: 'http://www.example.com'
        });
        return Promise.reject();
      }
      ToasterService.instance.pop({ type: ToastType.Success, text: Messages.resourceGovernanceServerSettingsSaveSuccessful() });
      return Promise.resolve(result);
    })
    .catch(() => {
      ToasterService.instance.pop({
        type: ToastType.Error,
        text: Messages.resourceGovernanceServerSettingsSaveFailed(),
        actionMessage: Messages.resourceGovernanceLearnMoreMessage(),
        actionHref: 'http://www.example.com'
      });
      return Promise.reject();
    });
}

export function openResourceGovernanceEditDefaultLimitsDialog(props: ResourceGovernanceEditDefaultLimitsDialogProps) {
  const initialDialogState: IResourceGovernanceServerSettings = {
    extractsConcurrencyLimit: props.extractsConcurrencyLimit,
    subscriptionsConcurrencyLimit: props.subscriptionsConcurrencyLimit,
    flowsConcurrencyLimit: props.flowsConcurrencyLimit,
    runtimeLimit: props.runtimeLimit,
    nextResetAt: props.nextResetAt,
  };

  const isActionDisabled = (props: ReactActionResourceGovernanceEditDefaultLimitsDialogProps, dialogState: IResourceGovernanceServerSettings) => {
    const rowInvalid = rowKeys.map(key => {
      const dialogValue = dialogState[key];
      // Technically we never expect to see an infinite value here, but better safe than sorry
      const invalidValue = dialogValue !== undefined && (isNaN(dialogValue) || !isFinite(dialogValue));
      return invalidValue;
    });
    return _.any(rowInvalid);
  };

  const modal = openReactActionDialog({
    bodyComponent: ResourceGovernanceDefaultLimitsDialog,
    dialogWidth: 500,
    title: props.title,
    initialBodyProps: props,
    initialDialogState,
    actionName: Messages.save(),
    action: saveDefaultLimitsSettings,
    actionDisabled: isActionDisabled,
    dismissDialogOnActionFailed: true,
    initiallyFocusedElement: (dialog => dialog.querySelector('input'))
  });
  return modal.result;
}